import React, { useState } from 'react';

import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';

const ClientsTable = ({ clients, onAction }) => {
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null); // { id, email }
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [localClients, setLocalClients] = useState(null);
  const [toast, setToast] = useState(null);

  const displayClients = localClients !== null ? localClients : clients;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleteLoading(true);
    try {
      const { error } = await supabase?.rpc('admin_soft_delete_user', {
        target_id: deleteModal?.id,
        delete_reason: 'admin_manual_delete',
      });
      if (error) throw error;
      setLocalClients(displayClients?.filter((c) => c?.id !== deleteModal?.id));
      showToast('Account eliminato');
      setDeleteModal(null);
    } catch (err) {
      showToast(err?.message || 'Errore durante l\'eliminazione', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      banned: 'bg-red-100 text-red-800',
    };
    return badges?.[status] || badges?.active;
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Attivo',
      suspended: 'Sospeso',
      banned: 'Bannato',
    };
    return labels?.[status] || status;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '—';
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return 'Adesso';
    if (diffMinutes < 60) return `${diffMinutes} minut${diffMinutes === 1 ? 'o' : 'i'} fa`;
    if (diffHours < 24) return `${diffHours} or${diffHours === 1 ? 'a' : 'e'} fa`;
    if (diffDays < 30) return `${diffDays} giorn${diffDays === 1 ? 'o' : 'i'} fa`;
    if (diffMonths < 12) return `${diffMonths} mes${diffMonths === 1 ? 'e' : 'i'} fa`;
    return `${diffYears} ann${diffYears === 1 ? 'o' : 'i'} fa`;
  };

  const handleAction = async (clientId, action) => {
    let confirmMessage = '';

    if (action === 'suspend') {
      confirmMessage = 'Sei sicuro di voler sospendere questo cliente?';
    } else if (action === 'ban') {
      confirmMessage = 'Sei sicuro di voler bannare questo cliente? Questa azione è reversibile.';
    } else if (action === 'reactivate') {
      confirmMessage = 'Sei sicuro di voler riattivare questo cliente?';
    }

    if (!window.confirm(confirmMessage)) return;

    setActionLoading(`${clientId}-${action}`);
    await onAction(clientId, action);
    setActionLoading(null);
  };

  if (displayClients?.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Icon name="Users" size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600">Nessun cliente trovato</p>
      </div>
    );
  }

  return (
    <>
      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${
            toast?.type === 'error' ? 'bg-red-600' : 'bg-green-600'
          }`}
        >
          {toast?.message}
        </div>
      )}
      {/* Delete confirmation modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="Trash2" size={20} className="text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Elimina account</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Sei sicuro di voler eliminare questo account? L'utente potrà registrarsi di nuovo con la stessa email, ma questa azione non è reversibile per l'account attuale.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteModal(null)}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {deleteLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Eliminazione...
                  </>
                ) : (
                  <>
                    <Icon name="Trash2" size={14} />
                    Elimina
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[180px]">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stato Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Richieste create
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ultima attività
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created at
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[280px]">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {displayClients?.map((client) => (
                <tr key={client?.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4 max-w-[180px]">
                    <div className="text-sm font-medium text-gray-900 truncate">{client?.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(
                        client?.account_status
                      )}`}
                    >
                      {getStatusLabel(client?.account_status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                    {client?.richieste_create ?? 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatRelativeTime(client?.ultima_attivita)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(client?.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium min-w-[280px]">
                    <div className="flex items-center justify-end gap-1.5 flex-nowrap">
                      {/* active: Sospendi (orange) + Banna (red) */}
                      {client?.account_status === 'active' && (
                        <>
                          <button
                            onClick={() => handleAction(client?.id, 'suspend')}
                            disabled={actionLoading === `${client?.id}-suspend`}
                            className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 transition-colors whitespace-nowrap"
                          >
                            {actionLoading === `${client?.id}-suspend` ? '...' : 'Sospendi'}
                          </button>
                          <button
                            onClick={() => handleAction(client?.id, 'ban')}
                            disabled={actionLoading === `${client?.id}-ban`}
                            className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors whitespace-nowrap"
                          >
                            {actionLoading === `${client?.id}-ban` ? '...' : 'Banna'}
                          </button>
                        </>
                      )}
                      {/* suspended: Riattiva (green) + Banna (red) */}
                      {client?.account_status === 'suspended' && (
                        <>
                          <button
                            onClick={() => handleAction(client?.id, 'reactivate')}
                            disabled={actionLoading === `${client?.id}-reactivate`}
                            className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition-colors whitespace-nowrap"
                          >
                            {actionLoading === `${client?.id}-reactivate` ? '...' : 'Riattiva'}
                          </button>
                          <button
                            onClick={() => handleAction(client?.id, 'ban')}
                            disabled={actionLoading === `${client?.id}-ban`}
                            className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-colors whitespace-nowrap"
                          >
                            {actionLoading === `${client?.id}-ban` ? '...' : 'Banna'}
                          </button>
                        </>
                      )}
                      {/* banned: Riattiva (green) only */}
                      {client?.account_status === 'banned' && (
                        <button
                          onClick={() => handleAction(client?.id, 'reactivate')}
                          disabled={actionLoading === `${client?.id}-reactivate`}
                          className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition-colors whitespace-nowrap"
                        >
                          {actionLoading === `${client?.id}-reactivate` ? '...' : 'Riattiva'}
                        </button>
                      )}
                      {/* Elimina — always visible for all statuses */}
                      <button
                        onClick={() => setDeleteModal({ id: client?.id, email: client?.email })}
                        className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-red-700 hover:bg-red-800 text-white transition-colors whitespace-nowrap flex items-center gap-1"
                        title="Elimina account"
                      >
                        <Icon name="Trash2" size={12} />
                        Elimina
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default ClientsTable;